import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import ListeConversations from './ListeConversations';
import FilConversation from './FilConversation';
import PanneauLateral from './PanneauLateral';
import NouvelleConversationModal from './NouvelleConversationModal';
import conversationService from '../../services/conversationService';
import stompClient from '../../services/stompClient';
import { getUser } from '../../variables/constants';
import { T } from './utils';

/**
 * Page Conversations : trois colonnes, largeur pleine.
 *
 * La liste et le fil partagent l'état des conversations : un message reçu sur la
 * file privée (`/user/queue/notifications`) remonte la conversation, met à jour
 * son aperçu et ses non-lus sans recharger la liste ; l'alerte elle-même vit
 * dans `AlertesConversations` (NavBar). La présence arrive sur
 * `/topic/presence` et met à jour les pastilles.
 */
const CLE_PANNEAU = 'cb.conversation.panneau';

export default function Conversations() {
  const { t } = useTranslation('conversations');
  const { id } = useParams();
  const navigate = useNavigate();
  const moi = getUser();
  const [conversations, setConversations] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [filtre, setFiltre] = useState('');
  const [etatConnexion, setEtatConnexion] = useState(stompClient.etat());
  const [modale, setModale] = useState(null); // null | 'nouvelle' | 'ajout'
  const [large, setLarge] = useState(() => window.innerWidth >= 1280);
  const [panneauOuvert, setPanneauOuvert] = useState(() => { try { return localStorage.getItem(CLE_PANNEAU) !== '0'; } catch { return true; } });
  const idRef = useRef(id);
  idRef.current = id;

  const actif = conversations.find((c) => c.conversationTrackingId === id) || null;

  const charger = useCallback(async () => {
    try {
      const l = await conversationService.lister();
      setConversations(l);
    } catch { /* toast global */ } finally { setChargement(false); }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // Connexion STOMP partagée, état pour le bandeau.
  useEffect(() => {
    stompClient.demarrer();
    const off = stompClient.surEtat(setEtatConnexion);
    const offReco = stompClient.surReconnexion(charger);
    return () => { off(); offReco(); };
  }, [charger]);

  useEffect(() => {
    const surRedim = () => setLarge(window.innerWidth >= 1280);
    window.addEventListener('resize', surRedim);
    return () => window.removeEventListener('resize', surRedim);
  }, []);

  const publierNonLus = useCallback(() => {
    conversationService.nonLus().then((n) => {
      window.dispatchEvent(new CustomEvent('cb:conversations-non-lus', { detail: n }));
    }).catch(() => {});
  }, []);

  // File privée : nouveau message ailleurs, nouvelle conversation.
  useEffect(() => {
    const off = stompClient.souscrire('/user/queue/notifications', (ev) => {
      if (ev.type === 'NOUVEAU_MESSAGE') {
        const p = ev.payload;
        const ouverte = p.conversationTrackingId === idRef.current && document.hasFocus();
        setConversations((prev) => {
          const existe = prev.some((c) => c.conversationTrackingId === p.conversationTrackingId);
          if (!existe) { charger(); return prev; }
          const maj = prev.map((c) => (c.conversationTrackingId === p.conversationTrackingId ? {
            ...c, apercu: p.apercu, apercuAuteur: p.auteur, apercuPriorite: p.priorite, dernierMessageLe: new Date().toISOString(),
            derniereSequence: p.sequence, nonLus: ouverte ? c.nonLus : c.nonLus + 1,
            urgentNonLu: !ouverte && p.priorite === 'URGENT',
          } : c));
          maj.sort((a, b) => (b.urgentNonLu - a.urgentNonLu) || ((b.dernierMessageLe || '') > (a.dernierMessageLe || '') ? 1 : -1));
          return maj;
        });
        // L'alerte (toast, son, titre, notification) est portée par AlertesConversations, sur toutes les pages.
      } else if (ev.type === 'CONVERSATION') {
        const c = ev.payload;
        setConversations((prev) => {
          const sans = prev.filter((x) => x.conversationTrackingId !== c.conversationTrackingId);
          return [c, ...sans];
        });
      }
    });
    const offPresence = stompClient.souscrire('/topic/presence', (ev) => {
      const p = ev.payload;
      setConversations((prev) => prev.map((c) => ({
        ...c,
        interlocuteurEnLigne: c.interlocuteurTrackingId === p.utilisateurTrackingId ? p.enLigne : c.interlocuteurEnLigne,
        membres: c.membres.map((m) => (m.utilisateurTrackingId === p.utilisateurTrackingId ? { ...m, enLigne: p.enLigne, derniereConnexion: p.derniereVue || m.derniereConnexion } : m)),
      })));
    });
    return () => { off(); offPresence(); };
  }, [charger, navigate]);

  // Ouvrir une conversation remet ses non-lus à zéro dans la liste.
  useEffect(() => {
    if (!id) return;
    setConversations((prev) => prev.map((c) => (c.conversationTrackingId === id ? { ...c, nonLus: 0, urgentNonLu: false } : c)));
  }, [id]);

  const surConversationMaj = useCallback((fn) => {
    setConversations((prev) => prev.map((c) => (c.conversationTrackingId === idRef.current ? fn(c) : c)));
  }, []);

  const creer = async (corps) => {
    try {
      const c = await conversationService.creer(corps);
      setConversations((prev) => [c, ...prev.filter((x) => x.conversationTrackingId !== c.conversationTrackingId)]);
      navigate(`/conversations/${c.conversationTrackingId}`);
    } catch (e) { toast.error(e?.response?.data?.message || t('page.ouvertureImpossible')); throw e; }
  };
  const ajouterMembres = async ({ membres }) => {
    try {
      const c = await conversationService.ajouterMembres(id, membres);
      setConversations((prev) => prev.map((x) => (x.conversationTrackingId === c.conversationTrackingId ? c : x)));
    } catch (e) { toast.error(e?.response?.data?.message || t('page.ajoutImpossible')); throw e; }
  };
  const quitter = async () => {
    try {
      await conversationService.quitter(id);
      setConversations((prev) => prev.filter((x) => x.conversationTrackingId !== id));
      navigate('/conversations');
    } catch (e) { toast.error(e?.response?.data?.message || t('page.quitterImpossible')); }
  };
  const basculerPanneau = () => setPanneauOuvert((p) => { try { localStorage.setItem(CLE_PANNEAU, p ? '0' : '1'); } catch { /* ignore */ } return !p; });

  return (
    <Layout title={t('page.titre')} breadcrumbs={false}>
      {/* Le <main> du Layout impose 28/32 px de marge ; la messagerie occupe toute la surface. */}
      <div style={{
        margin: '-28px -32px', height: 'calc(100vh - 56px - var(--cb-banniere-h, 0px))', display: 'flex', minWidth: 0, background: T.blanc,
        fontFamily: 'inherit', color: T.texte,
      }}>
        <ListeConversations
          conversations={conversations} chargement={chargement} actifId={id} filtre={filtre} surFiltre={setFiltre}
          surOuvrir={(cid) => navigate(`/conversations/${cid}`)} surNouvelle={() => setModale('nouvelle')}
        />
        <FilConversation
          conversation={actif} moi={moi} etatConnexion={etatConnexion} surConversationMaj={surConversationMaj}
          panneauOuvert={panneauOuvert && large} surBasculerPanneau={basculerPanneau} surNonLusChange={publierNonLus}
        />
        {actif && panneauOuvert && large && (
          <PanneauLateral conversation={actif} moiTrackingId={moi?.utilisateurTrackingId} surFermer={basculerPanneau}
            surAjouterMembres={() => setModale('ajout')} surQuitter={quitter} />
        )}
      </div>
      <NouvelleConversationModal
        ouverte={modale !== null} modeAjout={modale === 'ajout'} surFermer={() => setModale(null)}
        surCreer={modale === 'ajout' ? ajouterMembres : creer}
        exclure={modale === 'ajout' && actif ? actif.membres.filter((m) => m.actif).map((m) => m.utilisateurTrackingId) : []}
      />
    </Layout>
  );
}
